/**
 * Payout Structure Builder Component
 * 
 * Comprehensive tool for creating and analyzing pull tab game payout structures.
 * Provides live calculations, data import/export, validation, and detailed
 * statistical analysis to help users make informed decisions about pull tab games.
 * 
 * Features:
 * - Interactive payout table with add/edit/delete functionality
 * - Live calculations (total payout, hold percentage, odds analysis)
 * - Data import from clipboard (CSV-like formats)
 * - Export to CSV and JSON formats
 * - Example game data loader for quick testing
 * - Duplicate amount detection and merging
 * - Business rule validation and warnings
 * - Statistical analysis (median prize, odds ratios)
 * - Game saving to database with user association
 * - Responsive design with Tailwind CSS styling
 * 
 * This is a monolithic component that handles all payout-related functionality.
 * Future refactoring could split this into smaller, focused components.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { usePostApiGames } from "../../api/generated";
import { LocationPromptModal } from "../../components/LocationPrompt";
import { LocationContexts, type LocationContextType } from "../../utils/locationServices";
import { useLocationStore } from "../../stores/locationStore";
import { HelpIcon } from "../../components/ui/Tooltip";
import MoveableDividerInterface from "../../components/ui/MoveableDividerInterface";

// ---------- Types ----------

type PayoutRow = {
    id: string;
    amount: number; // currency dollars
    count: number; // integer >= 0
    isMajor: boolean; // true for major prizes (tracked individually), false for minor prizes (tracked with counters)
};

// ---------- Utils ----------

/**
 * Format number as currency with proper handling of edge cases
 * @param n - Number to format as currency
 * @returns Formatted currency string or "—" for invalid values
 */
const formatCurrency = (n: number | undefined | null) => {
    if (n == null || !isFinite(n)) return "—";
    return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });
};

/**
 * Format number as integer with thousands separators
 * @param n - Number to format as integer
 * @returns Formatted integer string or "—" for invalid values
 */
const formatInt = (n: number | undefined | null) => {
    if (n == null || !isFinite(n)) return "—";
    return Math.round(n).toLocaleString();
};

/**
 * Format number as percentage with one decimal place
 * @param v - Decimal value to format as percentage (0.1 = 10%)
 * @returns Formatted percentage string or "—" for invalid values
 */
const formatPct = (v: number | undefined | null) => {
    if (v == null || !isFinite(v)) return "—";
    return (v * 100).toFixed(1) + "%";
};

/**
 * Parse currency string into numeric value
 * @param raw - Raw string input that may contain currency symbols and formatting
 * @returns Parsed numeric value or NaN if invalid or empty
 */
const parseCurrency = (raw: string) => {
    const s = raw.replace(/[$,\s]/g, "");
    // Return NaN for empty strings to prevent them from becoming 0
    if (s === "") return NaN;
    const n = Number(s);
    return isFinite(n) ? n : NaN;
};

/**
 * Parse string into safe integer value
 * @param raw - Raw string input that may contain formatting characters
 * @returns Parsed integer value or NaN if invalid
 */
const parseIntSafe = (raw: string) => {
    const s = raw.replace(/[,\s]/g, "");
    const n = Math.floor(Number(s));
    return isFinite(n) ? n : NaN;
};

/**
 * Generate unique identifier for payout rows
 * @returns Random alphanumeric string suitable for React keys
 */
const uid = () => Math.random().toString(36).slice(2, 10);

/**
 * Calculate median prize amount from payout structure
 * 
 * Determines the median prize value by finding the prize amount that falls
 * at the 50th percentile when all winning tickets are considered.
 * 
 * @param payouts - Array of payout rows with amounts and counts
 * @returns Median prize amount or undefined if no valid payouts
 */
function medianPrize(payouts: PayoutRow[]): number | undefined {
    const totalWinners = payouts.reduce((a, r) => a + (r.count || 0), 0);
    if (totalWinners <= 0) return undefined;
    const asc = [...payouts].sort((a, b) => a.amount - b.amount);
    const target = Math.floor((totalWinners - 1) / 2) + 1; // 1-indexed position
    let cum = 0;
    for (const r of asc) {
        cum += r.count;
        if (cum >= target) return r.amount;
    }
    return asc[asc.length - 1]?.amount;
}

/**
 * Convert payout structure to CSV format
 * 
 * Exports payout data in standard CSV format suitable for Excel or other
 * spreadsheet applications.
 * 
 * @param payouts - Array of payout rows to export
 * @returns CSV string with header row and data
 */
function toCSV(payouts: PayoutRow[]): string {
    const header = "Award Amount,# of Awards per Deal";
    const lines = payouts
        .map((r) => `${r.amount},${r.count}`)
        .join("\n");
    return `${header}\n${lines}\n`;
}

/**
 * Trigger file download in browser
 * 
 * Creates a temporary download link and triggers the browser's download
 * functionality for the provided content.
 * 
 * @param filename - Name for the downloaded file
 * @param contents - File contents as string
 * @param mime - MIME type for the file (default: plain text)
 */
function download(filename: string, contents: string, mime = "text/plain;charset=utf-8") {
    const blob = new Blob([contents], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}


function parsePayoutText(text: string): PayoutRow[] {
    // Accept typical two-column pastes (Amount, Count).
    // Also try to be forgiving with separators (tabs/commas/pipes/spaces) and symbols ($, x).
    const rows: PayoutRow[] = [];
    const lines = text
        .replace(/[×x]/g, " ") // normalize multiplication symbols to space
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean);

    for (const line of lines) {
        // Try CSV/TSV first
        let cells = line.split(/\t|\s{2,}|,|\|/).map((c) => c.trim()).filter(Boolean);

        if (cells.length < 2) {
            // Fallback: extract numbers from the line in order
            const nums = (line.match(/\$?\s*-?[0-9]{1,3}(?:,[0-9]{3})*(?:\.[0-9]+)?/g) || []).map((m) => m.trim());
            if (nums.length >= 2) cells = [nums[0], nums[1]]; else continue;
        }

        const amtRaw = cells[0];
        const cntRaw = cells[1];
        const amount = parseCurrency(amtRaw);
        const count = parseIntSafe(cntRaw);
        if (!isNaN(amount) && !isNaN(count) && amount > 0 && count >= 0) {
            // Default to major for imported prizes (user can change later)
            rows.push({ id: uid(), amount, count, isMajor: true });
        }
    }

    return rows.sort((a, b) => b.amount - a.amount);
}

function mergeDuplicateAmounts(rows: PayoutRow[]): PayoutRow[] {
    const byAmt = new Map<number, { count: number, isMajor: boolean }>();
    for (const r of rows) {
        const existing = byAmt.get(r.amount);
        if (existing) {
            // When merging, keep as major if any instance was major
            byAmt.set(r.amount, { 
                count: existing.count + r.count, 
                isMajor: existing.isMajor || r.isMajor 
            });
        } else {
            byAmt.set(r.amount, { count: r.count, isMajor: r.isMajor });
        }
    }
    return [...byAmt.entries()]
        .map(([amount, { count, isMajor }]) => ({ id: uid(), amount, count, isMajor }))
        .sort((a, b) => b.amount - a.amount);
}

// ---------- Example Data ----------

const EXAMPLE_ROWS: PayoutRow[] = [
    { amount: 500, count: 2, id: uid(), isMajor: true },
    { amount: 250, count: 4, id: uid(), isMajor: true },
    { amount: 100, count: 10, id: uid(), isMajor: true },
    { amount: 50, count: 20, id: uid(), isMajor: true },
    { amount: 25, count: 40, id: uid(), isMajor: false },
    { amount: 20, count: 60, id: uid(), isMajor: false },
    { amount: 10, count: 120, id: uid(), isMajor: false },
    { amount: 5, count: 300, id: uid(), isMajor: false },
    { amount: 2, count: 600, id: uid(), isMajor: false },
    { amount: 1, count: 1000, id: uid(), isMajor: false },
].sort((a, b) => b.amount - a.amount);

// ---------- Main Component ----------

interface PayoutStructureBuilderProps {
    initialGameData?: any;
    mode?: 'create' | 'edit';
}

export default function PayoutStructureBuilder({ 
    initialGameData,
    mode = 'create'
}: PayoutStructureBuilderProps = {}) {
    const [dealName, setDealName] = useState("");
    const [ticketPrice, setTicketPrice] = useState<string>("");
    const [totalTickets, setTotalTickets] = useState<string>("");
    const [showLocationPrompt, setShowLocationPrompt] = useState(false);
    const [pendingGameData, setPendingGameData] = useState<any>(null);
    
    // Location services
    const locationPreferences = useLocationStore(state => state.preferences);
    const currentLocation = useLocationStore(state => state.currentLocation);
    const isLocationValid = useLocationStore(state => state.isLocationValid);
    const getFuzzedCoordinates = useLocationStore(state => state.getFuzzedCoordinates);
    const initializeLocation = useLocationStore(state => state.initialize);
    const requestLocation = useLocationStore(state => state.requestLocation);
    const [rows, setRows] = useState<PayoutRow[]>([
        { id: uid(), amount: 1, count: 0, isMajor: false },
        { id: uid(), amount: 2, count: 0, isMajor: false },
        { id: uid(), amount: 5, count: 0, isMajor: false },
        { id: uid(), amount: 10, count: 0, isMajor: false },
        { id: uid(), amount: 20, count: 0, isMajor: false },
        { id: uid(), amount: 25, count: 0, isMajor: false },
        { id: uid(), amount: 50, count: 0, isMajor: true },
        { id: uid(), amount: 100, count: 0, isMajor: true },
        { id: uid(), amount: 250, count: 0, isMajor: true },
        { id: uid(), amount: 500, count: 0, isMajor: true },
    ].sort((a, b) => b.amount - a.amount));

    const [showImport, setShowImport] = useState(false);
    const [importText, setImportText] = useState("");
    const [importPreview, setImportPreview] = useState<PayoutRow[] | null>(null);
    const [toast, setToast] = useState<string | null>(null);
    const [inputValues, setInputValues] = useState<Record<string, string>>({});

    // Feature flags
    const isImportEnabled = import.meta.env.DEV && import.meta.env.VITE_FEATURE_IMPORT_GAME === 'true';
    
    // Initialize with existing game data in edit mode
    useEffect(() => {
        if (mode === 'edit' && initialGameData) {
            console.log('Populating edit form with game data:', initialGameData);
            setDealName(initialGameData.name || "");
            setTicketPrice(initialGameData.ticketPrice || "");
            setTotalTickets(initialGameData.totalTickets?.toString() || "");
            
            if (initialGameData.payoutStructure?.tickets) {
                console.log('Populating payout structure:', initialGameData.payoutStructure.tickets);
                const payoutRows: PayoutRow[] = initialGameData.payoutStructure.tickets.map((ticket: any) => ({
                    id: uid(),
                    amount: parseFloat(ticket.amount), // API returns 'amount' not 'value'
                    count: ticket.quantity, // API returns 'quantity' not 'count'
                    isMajor: ticket.isMajor ?? true // Default to major if not specified (for backward compatibility)
                })).sort((a: PayoutRow, b: PayoutRow) => b.amount - a.amount);
                console.log('Setting payout rows:', payoutRows);
                setRows(payoutRows);
            }
        }
    }, [mode, initialGameData]);

    // React Query mutation for creating games
    const createGameMutation = usePostApiGames({
        mutation: {
            onSuccess: (data) => {
                setToast(`Game "${dealName}" saved successfully!`);
            },
            onError: (error) => {
                console.error('Error saving game:', error);
                setToast("Error saving game. Please try again.");
            }
        }
    });

    // Derived numbers
    const numTicketPrice = useMemo(() => parseCurrency(ticketPrice || ""), [ticketPrice]);
    const numTotalTickets = useMemo(() => parseIntSafe(totalTickets || ""), [totalTickets]);

    const totals = useMemo(() => {
        const totalWinners = rows.reduce((a, r) => a + (r.count || 0), 0);
        const totalPayout = rows.reduce((a, r) => a + (r.amount * (r.count || 0)), 0);
        const gross = isFinite(numTicketPrice) && isFinite(numTotalTickets) && numTicketPrice > 0 && numTotalTickets > 0
            ? numTicketPrice * numTotalTickets
            : NaN;
        const hold = isFinite(gross) && gross > 0 ? (gross - totalPayout) / gross : NaN;
        const losers = isFinite(numTotalTickets) ? (numTotalTickets - totalWinners) : NaN;
        const avgPrize = totalWinners > 0 ? totalPayout / totalWinners : NaN;
        const medPrize = medianPrize(rows);
        const topCount = rows.length ? rows[0].count : 0;
        const topPrizeDensity = isFinite(numTotalTickets) && numTotalTickets > 0 ? topCount / numTotalTickets : NaN;

        return { totalWinners, totalPayout, gross, hold, losers, avgPrize, medPrize, topPrizeDensity };
    }, [rows, numTicketPrice, numTotalTickets]);

    // Validation
    const hardErrors = useMemo(() => {
        const errs: string[] = [];
        if (rows.some((r) => r.amount <= 0 || r.count < 0 || !isFinite(r.amount) || !isFinite(r.count))) {
            errs.push("All amounts must be > 0 and counts must be ≥ 0.");
        }
        // Validate prize amounts don't exceed $100,000
        if (rows.some((r) => r.amount > 100000)) {
            errs.push("Prize amounts cannot exceed $100,000.");
        }
        // Validate total tickets don't exceed 100,000
        if (isFinite(numTotalTickets) && numTotalTickets > 100000) {
            errs.push("Total tickets cannot exceed 100,000.");
        }
        if (isFinite(numTotalTickets) && totals.totalWinners > numTotalTickets) {
            errs.push("Total winners exceed total tickets. Reduce counts or increase total tickets.");
        }
        // Ensure at least one major prize exists
        const hasMajorPrizes = rows.some(r => r.isMajor && r.count > 0);
        if (!hasMajorPrizes && rows.some(r => r.count > 0)) {
            errs.push("At least one major prize is required.");
        }
        // Check for duplicate amounts - only consider rows with valid current input
        const validAmounts = rows
            .map(r => {
                // If there's a current input value, use that to determine the effective amount
                const currentInput = inputValues[r.id];
                if (currentInput !== undefined) {
                    // If current input is empty, whitespace, or just non-numeric characters, exclude from duplicates
                    const trimmed = currentInput.trim();
                    if (trimmed === "" || trimmed === "$") {
                        return null; // Mark as no amount
                    }
                    // Parse the current input - if invalid, this row has no effective amount  
                    const parsedValue = parseCurrency(currentInput);
                    return isNaN(parsedValue) ? null : parsedValue;
                }
                // If no current input tracked, use the stored amount
                return r.amount;
            })
            .filter(amount => amount !== null); // Remove rows with no effective amount
        
        const duplicateAmounts = validAmounts.filter((amount, index) => validAmounts.indexOf(amount) !== index);
        if (duplicateAmounts.length > 0) {
            const uniqueDuplicates = [...new Set(duplicateAmounts)];
            errs.push(`Duplicate amounts found: $${uniqueDuplicates.join(', $')}. Each amount must be unique.`);
        }
        return errs;
    }, [rows, totals.totalWinners, numTotalTickets, inputValues]);

    const warnings = useMemo(() => {
        const warns: string[] = [];
        if (isFinite(totals.gross) && totals.gross > 0 && totals.totalPayout > totals.gross) {
            warns.push("Payout exceeds gross sales at this ticket price and deal size (negative hold).");
        }
        if (isFinite(totals.hold)) {
            if (totals.hold < 0.05) warns.push("Hold is unusually low (< 5%).");
            if (totals.hold > 0.5) warns.push("Hold is unusually high (> 50%).");
        }
        const shareMax = rows.reduce((m, r) => Math.max(m, totals.totalPayout > 0 ? (r.amount * r.count) / totals.totalPayout : 0), 0);
        if (shareMax > 0.3) warns.push("One prize tier is > 30% of total payout (concentrated risk).");
        return warns;
    }, [rows, totals.totalPayout, totals.gross, totals.hold]);


    // Sticky toast auto-hide
    useEffect(() => {
        if (!toast) return;
        const t = setTimeout(() => setToast(null), 2500);
        return () => clearTimeout(t);
    }, [toast]);

    // Initialize location store
    useEffect(() => {
        initializeLocation();
    }, [initializeLocation]);

    // ---------- Handlers ----------

    const addRow = () => {
        setRows((prev) => [{ id: uid(), amount: 1, count: 0, isMajor: true }, ...prev].sort((a, b) => b.amount - a.amount));
    };

    /**
     * Update amount for a specific payout row
     * 
     * Updates the amount value for a payout row. Only applies changes and merges
     * duplicates when the input is a valid number. Invalid inputs (like empty fields)
     * preserve the original amount to prevent accidental row deletion.
     * 
     * @param id - Unique identifier of the payout row
     * @param raw - Raw string input from the amount field
     */
    const updateAmount = (id: string, raw: string) => {
        // Always track the input field value, regardless of validity
        setInputValues(prev => ({ ...prev, [id]: raw }));
        
        const val = parseCurrency(raw);
        setRows((prev) => {
            if (isNaN(val) || val <= 0) {
                // For invalid or zero/negative inputs, don't change the amount value
                return prev;
            }
            // For valid inputs, just update the amount - no merging
            return prev.map((r) => (r.id === id ? { ...r, amount: val } : r));
        });
        
        if (!isNaN(val) && val > 0) {
            setToast("Updated amount");
        }
    };

    /**
     * Update count for a specific payout row
     * 
     * Updates the count (number of winners) for a payout row. Sets count to 0
     * for invalid or negative inputs to ensure data integrity.
     * 
     * @param id - Unique identifier of the payout row
     * @param raw - Raw string input from the count field
     */
    const updateCount = (id: string, raw: string) => {
        const val = parseIntSafe(raw);
        setRows((prev) => prev.map((r) => (r.id === id ? { ...r, count: isNaN(val) || val < 0 ? 0 : val } : r)).sort((a, b) => b.amount - a.amount));
    };

    /**
     * Toggle major/minor status for a specific payout row
     * 
     * @param id - Unique identifier of the payout row
     * @param isMajor - New major/minor status
     */
    const updateIsMajor = (id: string, isMajor: boolean) => {
        setRows((prev) => prev.map((r) => (r.id === id ? { ...r, isMajor } : r)));
    };


    const clearAll = () => {
        if (!confirm("Clear all payout rows?")) return;
        setRows([]);
    };

    const loadExample = () => {
        setRows(EXAMPLE_ROWS.map((r) => ({ ...r, id: uid() })));
        setTicketPrice("1.00");
        setTotalTickets("6000");
    };

    const exportCSV = () => {
        download("payout-structure.csv", toCSV(rows), "text/csv;charset=utf-8");
    };

    const exportJSON = () => {
        const payload = {
            dealName: dealName || undefined,
            ticketPrice: isFinite(numTicketPrice) ? numTicketPrice : undefined,
            totalTickets: isFinite(numTotalTickets) ? numTotalTickets : undefined,
            payouts: rows.map(({ amount, count, isMajor }) => ({ amount, count, isMajor })),
        };
        download("payout-structure.json", JSON.stringify(payload, null, 2));
    };

    const handleImportPreview = () => {
        const parsed = parsePayoutText(importText);
        setImportPreview(parsed);
    };

    // Check for duplicate amounts in import preview
    const importHasDuplicates = useMemo(() => {
        if (!importPreview) return false;
        const amounts = importPreview.map(r => r.amount);
        const duplicateAmounts = amounts.filter((amount, index) => amounts.indexOf(amount) !== index);
        return duplicateAmounts.length > 0;
    }, [importPreview]);

    const commitImport = (mode: "replace" | "append") => {
        if (!importPreview) return;
        setRows((prev) => {
            if (mode === "replace") {
                return importPreview;
            } else {
                // For append mode, merge duplicates between existing and imported data
                return mergeDuplicateAmounts([...prev, ...importPreview]);
            }
        });
        setImportText("");
        setImportPreview(null);
        setShowImport(false);
        setToast("Imported payout rows");
    };

    const saveGame = () => {
        if (!dealName.trim()) {
            setToast("Please enter a deal name");
            return;
        }
        if (!isFinite(numTicketPrice) || numTicketPrice <= 0) {
            setToast("Please enter a valid ticket price");
            return;
        }
        if (!isFinite(numTotalTickets) || numTotalTickets <= 0) {
            setToast("Please enter a valid total tickets count");
            return;
        }
        if (rows.length === 0 || !rows.some(r => r.count > 0)) {
            setToast("Please add at least one payout tier with count > 0");
            return;
        }
        if (hardErrors.length > 0) {
            setToast("Please fix validation errors before saving");
            return;
        }

        const payoutData = rows
            .filter(r => r.count > 0)
            .map(r => ({
                amount: r.amount.toString(),
                quantity: r.count,
                isMajor: r.isMajor,
            }));

        const gameData = {
            name: dealName.trim(),
            totalTickets: numTotalTickets,
            ticketPrice: numTicketPrice.toFixed(2),
            totalPayout: totals.totalPayout.toFixed(2),
            holdPercentage: (totals.hold * 100).toFixed(2),
            payoutStructure: {
                tickets: payoutData.map(row => ({
                    amount: row.amount,
                    quantity: row.quantity,
                    isMajor: row.isMajor
                }))
            }
        };

        // Check if we should prompt for location sharing (only for new games)
        if (mode === 'create' && 
            locationPreferences.locationSharingEnabled && 
            locationPreferences.autoShareOnCreation && 
            (!currentLocation || !isLocationValid())) {
            
            // Store game data for later and show location prompt
            setPendingGameData(gameData);
            setShowLocationPrompt(true);
            return;
        }

        // Save game with or without location data
        const finalGameData = {
            ...gameData,
            ...(shouldIncludeLocation() ? getLocationData() : {})
        };

        createGameMutation.mutate({
            data: finalGameData
        });
    };

    const shouldIncludeLocation = (): boolean => {
        return mode === 'create' && 
               locationPreferences.locationSharingEnabled && 
               currentLocation !== null && 
               isLocationValid();
    };

    const getLocationData = () => {
        if (!currentLocation || !isLocationValid()) return {};
        
        const fuzzedCoords = getFuzzedCoordinates();
        if (!fuzzedCoords) return {};
        
        return {
            location: {
                latitude: fuzzedCoords.latitude,
                longitude: fuzzedCoords.longitude,
                accuracy: currentLocation.coordinates.accuracy,
                fuzzingRadius: locationPreferences.fuzzingRadius
            }
        };
    };

    // Location prompt handlers
    const handleLocationGranted = (position: GeolocationPosition) => {
        // Location will be stored in the location store by the LocationPrompt component
        // Now save the game with location data
        const finalGameData = {
            ...pendingGameData,
            ...(shouldIncludeLocation() ? getLocationData() : {})
        };
        
        createGameMutation.mutate({
            data: finalGameData
        });
        
        setPendingGameData(null);
        setShowLocationPrompt(false);
    };

    const handleLocationDenied = () => {
        // Save game without location data
        if (pendingGameData) {
            createGameMutation.mutate({
                data: pendingGameData
            });
        }
        
        setPendingGameData(null);
        setShowLocationPrompt(false);
    };

    const handleLocationFallback = () => {
        // Same as denied - save without location
        handleLocationDenied();
    };

    const breakEvenTicketPrice = useMemo(() => {
        if (!isFinite(numTotalTickets) || numTotalTickets <= 0) return NaN;
        return totals.totalPayout / numTotalTickets;
    }, [totals.totalPayout, numTotalTickets]);

    // Hold status color
    const holdState: "ok" | "warn" | "error" | "na" = useMemo(() => {
        if (!isFinite(totals.hold)) return "na";
        if (totals.hold < 0) return "error";
        if (totals.hold < 0.05 || totals.hold > 0.5) return "warn";
        return "ok";
    }, [totals.hold]);

    return (
        <div className="text-slate-900">
            {/* Header */}
            <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/80 backdrop-blur">
                <div className="mx-auto max-w-6xl px-4 py-3">
                    <h1 className="text-2xl font-bold text-tp-gold mb-3">Create New Game</h1>
                    <div className="flex flex-wrap items-center gap-3">
                        <input
                            className="flex-1 min-w-[240px] rounded-2xl border border-slate-300 bg-white px-4 py-2 text-lg font-semibold shadow-sm outline-none focus:ring-2 focus:ring-indigo-500"
                            placeholder="Deal name (e.g., Lucky Sevens)"
                            value={dealName}
                            onChange={(e) => setDealName(e.target.value)}
                        />

                        <div className="ml-auto flex items-center gap-2">
                            <button onClick={loadExample} className="rounded-xl border border-tp-gold px-3 py-2 text-sm shadow-sm text-tp-gold hover:bg-tp-gold hover:text-white transition-colors">Start from example</button>
                            {isImportEnabled && (
                                <button onClick={() => setShowImport(true)} className="rounded-xl border px-3 py-2 text-sm shadow-sm hover:bg-slate-50">Import</button>
                            )}
                            <button onClick={saveGame} disabled={createGameMutation.isPending || !dealName.trim() || !isFinite(numTicketPrice) || !isFinite(numTotalTickets) || hardErrors.length > 0} className={`rounded-xl px-3 py-2 text-sm shadow-sm ${!createGameMutation.isPending && dealName.trim() && isFinite(numTicketPrice) && isFinite(numTotalTickets) && hardErrors.length === 0 ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-slate-200 text-slate-500"}`}>
                                {createGameMutation.isPending ? "Saving..." : "Save"}
                            </button>
                        </div>
                    </div>
                    
                    {/* Validation errors in header */}
                    {(hardErrors.length > 0 || warnings.length > 0) && (
                        <div className="mt-3 space-y-2">
                            {hardErrors.map((e, i) => (
                                <div key={i}
                                     className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">{e}</div>
                            ))}
                            {warnings.map((w, i) => (
                                <div key={i}
                                     className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">{w}</div>
                            ))}
                        </div>
                    )}
                </div>
            </header>

            {/* Meta + Summary */}
            <section className="mx-auto max-w-6xl px-4 py-6">
                <div className="grid grid-cols-1 gap-4 tablet:grid-cols-3">
                    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-tp-brand">Deal
                            settings</h3>
                        <label className="mb-3 block">
                            <div className="mb-1 text-sm text-slate-600">Ticket price</div>
                            <input
                                type="text"
                                inputMode="decimal"
                                placeholder="$1.00"
                                className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 outline-none focus:ring-2 focus:ring-indigo-500"
                                value={ticketPrice}
                                onChange={(e) => setTicketPrice(e.target.value)}
                            />
                        </label>
                        <label className="mb-3 block">
                            <div className="mb-1 text-sm text-slate-600">Total tickets per deal</div>
                            <input
                                type="number"
                                inputMode="numeric"
                                placeholder="6000"
                                min="1"
                                max="100000"
                                className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 outline-none focus:ring-2 focus:ring-indigo-500"
                                value={totalTickets}
                                onChange={(e) => setTotalTickets(e.target.value)}
                            />
                        </label>
                        <div className="mb-4 text-xs text-slate-500">Common sizes: <span
                            className="rounded bg-slate-100 px-1.5 py-0.5">2400</span> <span
                            className="rounded bg-slate-100 px-1.5 py-0.5">3000</span> <span
                            className="rounded bg-slate-100 px-1.5 py-0.5">4000</span> <span
                            className="rounded bg-slate-100 px-1.5 py-0.5">6000</span> <span
                            className="rounded bg-slate-100 px-1.5 py-0.5">10000</span></div>

                    </div>
                </div>
            </section>

            {/* Payout Tiers */}
            <section className="mx-auto max-w-6xl px-4 pb-8">
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <div className="mb-4 flex items-center justify-between">
                        <h3 className="text-sm font-semibold uppercase tracking-wide text-tp-brand">Payout tiers</h3>
                        <div className="flex items-center gap-2">
                            <button onClick={addRow}
                                    className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm hover:bg-slate-50">Add
                                row
                            </button>
                            <button onClick={clearAll}
                                    className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm hover:bg-slate-50">Clear
                            </button>
                        </div>
                    </div>

                    {rows.length === 0 && (
                        <div className="py-8 text-center text-slate-500">
                            No rows yet. Add a row, import, or <button onClick={loadExample}
                                                                       className="underline">start from the
                            example</button>.
                        </div>
                    )}

                    {rows.length > 0 && (
                        <>
                            <MoveableDividerInterface
                                payouts={rows}
                                onUpdatePayouts={setRows}
                            />
                        </>
                    )}
                </div>
            </section>

            {/* Payout Grid */}
            <section className="mx-auto max-w-6xl px-4 pb-24">
                <div className="tablet:col-span-2 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="mb-3 flex items-center gap-2">
                        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Live summary</h3>
                        <span
                            className={`rounded-full px-2 py-0.5 text-xs font-semibold ${holdState === "ok" ? "bg-emerald-100 text-emerald-700" : holdState === "warn" ? "bg-amber-100 text-amber-700" : holdState === "error" ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-700"}`}>{holdState === "ok" ? "Healthy hold" : holdState === "warn" ? "Review hold" : holdState === "error" ? "Negative hold" : "Hold N/A"}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 tablet:grid-cols-3 desktop:grid-cols-6">
                        <SummaryItem label="Total winners" value={formatInt(totals.totalWinners)}/>
                        <SummaryItem label="Total payout" value={formatCurrency(totals.totalPayout)}/>
                        <SummaryItem label="Gross sales" value={formatCurrency(totals.gross)}/>
                        <SummaryItem label="Hold" value={isFinite(totals.hold) ? formatPct(totals.hold) : "—"}/>
                        <SummaryItem label="Losers per deal"
                                     value={isFinite(totals.losers) ? formatInt(totals.losers) : "—"}/>
                        <SummaryItem label="Avg prize (winners)"
                                     value={isFinite(totals.avgPrize) ? formatCurrency(totals.avgPrize) : "—"}/>
                        <SummaryItem label="Median prize"
                                     value={totals.medPrize != null ? formatCurrency(totals.medPrize) : "—"}/>
                        <SummaryItem label="Top prize density"
                                     value={isFinite(totals.topPrizeDensity) ? formatPct(totals.topPrizeDensity) : "—"}/>
                        <SummaryItem label="Break-even price"
                                     value={isFinite(breakEvenTicketPrice) ? formatCurrency(breakEvenTicketPrice) : "—"}/>
                    </div>

                </div>
            </section>

            {/* Import Drawer (simple) */}
            {isImportEnabled && showImport && (
                <div className="fixed inset-0 z-40 flex items-end tablet:items-center tablet:justify-center">
                    <div className="absolute inset-0 bg-slate-900/40" onClick={() => setShowImport(false)}/>
                    <div
                        className="relative z-50 w-full max-w-2xl rounded-t-2xl bg-white p-4 shadow-2xl tablet:rounded-2xl">
                        <div className="mb-3 flex items-center justify-between">
                            <h3 className="text-lg font-semibold">Import payout rows</h3>
                            <button onClick={() => setShowImport(false)}
                                    className="rounded-lg px-2 py-1 text-slate-500 hover:bg-slate-100">Close
                            </button>
                        </div>
                        <p className="mb-3 text-sm text-slate-600">Paste two columns: <span
                            className="font-mono">Amount</span>, <span className="font-mono">Count</span>. Tabs, commas,
                            or pipes are fine. Examples: <span className="font-mono">500 2</span>, <span
                                className="font-mono">$100, 10</span>.</p>
                        <textarea
                            value={importText}
                            onChange={(e) => setImportText(e.target.value)}
                            placeholder={"$500\t2\n$250\t4\n$100\t10"}
                            className="h-40 w-full resize-y rounded-xl border border-slate-300 bg-white p-3 font-mono text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        <div className="mt-3 flex items-center gap-2">
                            <button onClick={handleImportPreview}
                                    className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm hover:bg-slate-50">Preview
                            </button>
                            {importPreview &&
                                <span className="text-xs text-slate-500">{importPreview.length} rows parsed</span>}
                        </div>

                        {importPreview && (
                            <div className="mt-4">
                                {importHasDuplicates && (
                                    <div className="mb-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
                                        Duplicate amounts detected in import data. Each amount must be unique.
                                    </div>
                                )}
                                <div className="overflow-x-auto rounded-xl border border-slate-200">
                                    <table className="min-w-full text-left text-sm">
                                        <thead className="bg-slate-100">
                                        <tr className="text-slate-600">
                                            <th className="px-3 py-2 font-medium">Amount</th>
                                            <th className="px-3 py-2 font-medium"># of awards</th>
                                            <th className="px-3 py-2 font-medium">Type</th>
                                            <th className="px-3 py-2 font-medium">Subtotal</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {importPreview.map((r) => (
                                            <tr key={r.id} className="border-t border-slate-100">
                                                <td className="px-3 py-2">{formatCurrency(r.amount)}</td>
                                                <td className="px-3 py-2">{formatInt(r.count)}</td>
                                                <td className="px-3 py-2">
                                                    <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${r.isMajor ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                                                        {r.isMajor ? 'Major' : 'Minor'}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2">{formatCurrency(r.amount * r.count)}</td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
                            <button onClick={() => commitImport("append")} disabled={!importPreview || importHasDuplicates} className={`rounded-xl px-3 py-2 text-sm shadow-sm ${importPreview && !importHasDuplicates ? "border border-slate-300 bg-white hover:bg-slate-50" : "border border-slate-200 bg-slate-100 text-slate-400"}`}>Append & merge</button>
                            <button onClick={() => commitImport("replace")} disabled={!importPreview || importHasDuplicates} className={`rounded-xl px-3 py-2 text-sm shadow-sm ${importPreview && !importHasDuplicates ? "bg-indigo-600 text-white hover:bg-indigo-700" : "bg-slate-200 text-slate-400"}`}>Replace current</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Tiny toast */}
            {toast && (
                <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 shadow-lg">
                    {toast}
                </div>
            )}

            {/* Footer spacer */}
            <div className="h-8" />
            
            {/* Location Prompt Modal */}
            <LocationPromptModal
                isOpen={showLocationPrompt}
                onClose={() => setShowLocationPrompt(false)}
                context={LocationContexts.GAME_CREATION}
                contextType="GAME_CREATION"
                onLocationGranted={handleLocationGranted}
                onLocationDenied={handleLocationDenied}
                onFallback={handleLocationFallback}
            />
        </div>
    );
}

function SummaryItem({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
            <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
            <div className="mt-1 text-base font-semibold tabular-nums">{value}</div>
        </div>
    );
}
